import { h, Component } from 'preact';
import $ from '../utils/net';
import Config from './config';
import PFeed from './prematch-feed';
import User from './user';
import Ticket from './ticket';
import CashoutService from './cashout';
import { CasinoFavourites, GameEntry } from './casino-feed';
import { BonusNotifierService } from './bonus-notifier';
import { makeReactive } from '../utils/reactive-store';

// Interfacce per la tipizzazione
export interface PopupData {
    path: string;
    data: any;
}

export interface PageData {
    'mobile-ticket-open': boolean;
    'open-tournaments': Set<string>;
    'lang': string;
    'selected-odds': Record<string, any>;
    'menu-toggled': Set<string>;
    'tournament-data': Record<string, any>;
    'currency': string;
    'popup': PopupData | false;
    'mobile': boolean;
    'current-game': GameEntry | null;
}

// Mock o riferimento per USER_POPUP se non importato (spesso gestito globalmente o in altri file)
declare const USER_POPUP: any;

export default class PageDataFeed extends Component<any, any> {
    // Definizione delle proprietà statiche
    static data: PageData = makeReactive({
        'mobile-ticket-open': false,
        'open-tournaments': new Set<string>(),
        'lang': 'it',
        'selected-odds': {},
        'menu-toggled': new Set<string>(),
        'tournament-data': {},
        'currency': 'EUR',
        'popup': false,
        'mobile': typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
        'current-game': null
    });

    static events: Record<string, Array<(...args: any[]) => void>> = {
        'lang': [],
        'current-game': []
    };

    static widgetsState: Record<string, boolean> = makeReactive({});
    static user = User;
    /**
     * @deprecated
     */
    static timefilter: number = Infinity;
    static favourites: Record<string, number> = JSON.parse(
        (typeof window !== 'undefined' && window.safeLocalStorage.getItem('favourites')) || '{}'
    );
    static carouselPromise: Promise<any> | null = null;

    /**
     * Rerender globale dell'applicazione. 
     * Viene sovrascritta nell'entry point (es. index.tsx) per triggerare l'aggiornamento della root.
     */
    static rerender: () => void = () => {};

    // --- Metodi Mobile Ticket ---
    static toggleMobileTicket(): void {
        PageDataFeed.data['mobile-ticket-open'] = !PageDataFeed.data['mobile-ticket-open'];
        PageDataFeed.rerender();
    }

    static isMobileTicketOpen(): boolean {
        return PageDataFeed.data['mobile-ticket-open'];
    }

    // --- Metodi Scommesse / Tornei ---
    static openTournament(id: string, options: any, cb?: (tag: string, data: any) => void): void {
        PageDataFeed.data['open-tournaments'].add(id);
        PageDataFeed.rerender();
        PFeed.callTournamentMono(id, options, (tag: string, data: any) => {
            PageDataFeed.data['tournament-data'][id] = data;
            PageDataFeed.rerender();
            if (cb) cb(tag, data);
        });
    }

    static openNextEvents(sport: string, hour: string | number, options: any, cb: (tag: string, data: any) => void): void {
        PageDataFeed.rerender();
        PFeed.callNextEvents(sport, hour, {}, (tag: string, data: any) => {
            Object.entries(data).forEach(([tPath, tournament]) => {
                PageDataFeed.data['open-tournaments'].add(tPath);
                PageDataFeed.data['tournament-data'][tPath] = tournament;
            });
            PageDataFeed.rerender();
            cb(tag, data);
        });
    }

    static openMostPlayedEvents(sport: string, options: any, cb: (tag: string, data: any) => void): void {
        PageDataFeed.rerender();
        PFeed.callMostPlayedEvents(sport, (tag: string, data: any) => {
            Object.entries(data).forEach(([tPath, tournament]) => {
                PageDataFeed.data['open-tournaments'].add(tPath);
                PageDataFeed.data['tournament-data'][tPath] = tournament;
            });
            PageDataFeed.rerender();
            cb(tag, data);
        });
    }

    static openMostBetEventsMerged(sport: string, options: any, cb: (tag: string, data: any) => void): void {
        PageDataFeed.rerender();
        PFeed.callMostBetEvents(sport, (tag: string, data: any) => {
            const tournaments = Object.values(data);
            const firstTournament: any = tournaments[0];
            const mostPlayedCard = tournaments.reduce((c: any, tournament: any) => {
                c.sub = { ...c.sub, ...tournament.sub };
                return c;
            }, { ...(firstTournament || {}), name: "Most Bets" });

            PageDataFeed.rerender();
            cb(tag, mostPlayedCard);
        });
    }

    static openMostPlayedEventsMerged(sport: string, options: any, cb: (tag: string, data: any) => void): void {
        PageDataFeed.rerender();
        PFeed.callMostPlayedEvents(sport, (tag: string, data: any) => {
            const tournaments = Object.values(data);
            const firstTournament: any = tournaments[0];
            const mostPlayedCard = tournaments.reduce((c: any, tournament: any) => {
                c.sub = { ...c.sub, ...tournament.sub };
                return c;
            }, { ...(firstTournament || {}), name: "Most Played" });

            PageDataFeed.rerender();
            cb(tag, mostPlayedCard);
        });
    }

    static openNextEventsMerged(sport: string, hour: string | number, options: any, cb: (tag: string, data: any) => void): void {
        PageDataFeed.rerender();
        PFeed.callNextEvents(sport, hour, {}, (tag: string, data: any) => {
            const tournaments = Object.values(data);
            const firstTournament: any = tournaments[0];
            if (firstTournament) firstTournament.sub = Object.assign({}, firstTournament.sub);

            const nextEventsCard = tournaments.reduce((c: any, tournament: any) => {
                c.sub = Object.assign(c.sub, tournament.sub);
                return c;
            }, { ...(firstTournament || {}), name: "Next Events" });

            PageDataFeed.rerender();
            cb(tag, nextEventsCard);
        });
    }

    static replaceTournamentsWith(id: string, cb: (tag: string, data: any) => void, options = {}): void {
        PageDataFeed.data['open-tournaments'].clear();
        PageDataFeed.openTournament(id, options, cb);
    }

    static closeTournament(id: string): void {
        PageDataFeed.data['open-tournaments'].delete(id);
        PageDataFeed.rerender();
    }

    static openTournaments(ids: string[]): void {
        ids.forEach(id => PageDataFeed.data['open-tournaments'].add(id));
        PageDataFeed.rerender();
        ids.forEach(id => PFeed.callTournamentMono(id, {}, (tag: string, data: any) => {
            PageDataFeed.data['tournament-data'][id] = data;
            PageDataFeed.rerender();
        }));
    }

    static closeTournaments(ids: string[]): void {
        ids.forEach(id => PageDataFeed.data['open-tournaments'].delete(id));
        PageDataFeed.rerender();
    }

    static tournamentIsOpen(id: string): boolean {
        return PageDataFeed.data['open-tournaments'].has(id);
    }

    static mapTournaments<T>(cb: (item: { tournament: any; card: any }, index: number) => T): T[] {
        return Array.from(PageDataFeed.data['open-tournaments']).map((tId, idx) => cb({
            tournament: PFeed.NStorage.get(tId),
            card: PageDataFeed.tournamentData(tId)
        }, idx));
    }

    static openCategory(id: string): void {
        const category = PFeed.NStorage.get(id);
        if (category && category.sub) {
            PageDataFeed.openTournaments(Object.values(category.sub).map((t: any) => t.id));
        }
    }

    static closeAllCategories(): void {
        PageDataFeed.data['open-tournaments'].clear();
        PageDataFeed.rerender();
    }

    static replaceOpenCategory(id: string): void {
        PageDataFeed.data['open-tournaments'].clear();
        const category = PFeed.NStorage.get(id);
        if (category && category.sub) {
            PageDataFeed.openTournaments(Object.values(category.sub).map((t: any) => t.id).reverse());
        }
    }

    static mapCategories<T>(cb: (item: { category: any }, index: number) => T): T[] {
        const categories = Array.from(new Set(
            Array.from(PageDataFeed.data['open-tournaments']).map(tId => PFeed.NStorage.get(tId)?.parent)
        )).filter(id => !!id);
        return categories.map((id, idx) => cb({ category: PFeed.NStorage.get(id!) }, idx));
    }

    static tournamentData(tId: string): any {
        return PageDataFeed.data['tournament-data'][tId];
    }

    // --- Preferiti ---
    static setFavourite(id: string): void {
        PageDataFeed.favourites[id] = Date.now();
        if (Object.values(PageDataFeed.favourites).length > 100) {
            PageDataFeed.favourites = Object.entries(PageDataFeed.favourites)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 50)
                .reduce((c: Record<string, number>, [key, val]) => {
                    c[key] = val;
                    return c;
                }, {});
        }
        window.safeLocalStorage.setItem("favourites", JSON.stringify(PageDataFeed.favourites));
    }

    static deleteFavourite(id: string): void {
        delete PageDataFeed.favourites[id];
        window.safeLocalStorage.setItem("favourites", JSON.stringify(PageDataFeed.favourites));
    }

    static isFavourite(id: string): boolean {
        return !!PageDataFeed.favourites[id];
    }

    static toggleFavourite(id: string): void {
        if (PageDataFeed.favourites[id]) PageDataFeed.deleteFavourite(id); 
        else PageDataFeed.setFavourite(id);
    }

    static mapFavourites<T>(cb: (id: string) => T): T[] {
        return Object.keys(PageDataFeed.favourites).map(cb);
    }

    // --- Menu Status ---
    static menuToggle(id: string): void {
        if (PageDataFeed.data['menu-toggled'].has(id))
            PageDataFeed.data['menu-toggled'].delete(id);
        else
            PageDataFeed.data['menu-toggled'].add(id);

        PageDataFeed.rerender();
    }

    static menuIsOpen(id: string): boolean {
        return PageDataFeed.data['menu-toggled'].has(id);
    }

    // --- Lang & Currency ---
    static getLang(): string {
        return PageDataFeed.data['lang'];
    }

    static setLang(l: string): void {
        PageDataFeed.data['lang'] = l;
        PageDataFeed.events['lang'].forEach(cb => cb('lang', l));
    }

    static onLangChange(cb: (tag: string, val: string) => void): void {
        PageDataFeed.events['lang'].push(cb);
    }

    static getCurrency(): string {
        return PageDataFeed.data['currency'];
    }

    // --- Popups ---
    static setPopup(path: string, optional: any = {}): void {
        PageDataFeed.data.popup = { path, data: optional };
        PageDataFeed.rerender();
    }

    static getPopup(): PopupData | false {
        return PageDataFeed.data.popup;
    }

    static closePopup(): void {
        PageDataFeed.data.popup = false;
        PageDataFeed.rerender();
    }

    // --- Auth Actions ---
    static login(username: string, password: string): void {
        PageDataFeed.user.login(username, password).then((result) => {
            if (result && result.success) {
                PageDataFeed.closePopup();
                if (typeof USER_POPUP !== 'undefined') USER_POPUP.userLogged();
            } else {
                const currentPopup = PageDataFeed.data.popup;
                if (currentPopup) {
                    PageDataFeed.setPopup(currentPopup.path, { error: "login-error" });
                }
                if (typeof USER_POPUP !== 'undefined') USER_POPUP.route();
            }
        }).catch(() => {
             const currentPopup = PageDataFeed.data.popup;
             if (currentPopup) PageDataFeed.setPopup(currentPopup.path, { error: "login-error" });
        });
    }

    static logout(): void {
        PageDataFeed.user.logout().then(() => PageDataFeed.rerender());
        if (typeof USER_POPUP !== 'undefined') USER_POPUP.userLoggedOut();
    }

    static loadSettings(): Promise<void> {
        return $.getJSON(Config.softionAPI + '/prematch/settings' + (Config.publickey ? '?public=' + Config.publickey : ''), (err: any, config: any) => {
            if (err || !config) return;
            
            // Aggiornamento Configurazione Globale
            Config.betting = { ...Config.betting, ...config };
            Config.cashoutEnable = config['cashout-enable'];
            Config.cashoutLiveCommission = config['cashout-live-commission'];
            Config.defaultCurrency = config['currency'];
            
            PageDataFeed.data.currency = config['currency'];
            
            Ticket.reloadSettings();
            CashoutService.loadConfig();
            PageDataFeed.rerender();
        });
    }

    static userLoginAction(): Promise<void> {
        return User.isSessionOpen().then((data) => {
            if (data && data.lang) {
                PFeed.lang = data.lang;
                PageDataFeed.data.lang = data.lang;
            }
            if (data && data.ticketprint_url) {
                (Config as any).ticketPrintAPI = data.ticketprint_url;
            }
            PageDataFeed.rerender();
        });
    }

    // --- Filtri e Formati ---
    static setTimeFilter(value: number | null): void {
        PageDataFeed.timefilter = value || Infinity;
        PageDataFeed.rerender();
    }

    static setOddsFormat(formatType: string): void {
        Config.betting['odds-format'] = formatType;
        PageDataFeed.rerender();
    }

    static currentOddsFormat(): string {
        return Config.betting['odds-format'];
    }

    static setUserToken(token: string, clubName: string): Promise<any> {
        return new Promise((res, rej) => {
            $.getJSON(token + '?json&public=' + clubName, (err: any, config: any) => {
                if (err || !config) return rej(err);
                User.isSessionOpen();
                res(config);
            }, { credentials: 'include' });
        });
    }

    // --- Stampe e Widget ---
    static printTicket(ticketInfo: { code: string }): void {
        try {
            const url = (Config as any).ticketPrintAPI + ticketInfo.code + '&lang=' + Config.lang;
            const myWindow = window.open(
                url,
                'ticket-' + ticketInfo.code,
                'width=350,height=600,location=no,menubar=no,status=no,toolbar=no,scrollbars=yes'
            );
            if (myWindow) myWindow.focus();
        } catch (e) {
            console.error("Popup bloccati o errore di stampa", e);
        }
    }

    static printReserved(id: string | number, options: { decimal?: boolean } = {}): void {
        try {
            const url = (Config as any).reservedPrintAPI + id + (options.decimal ? "&decimal=1" : '');
            const myWindow = window.open(
                url,
                'ticket-' + id,
                'width=350,height=600,location=no,menubar=no,status=no,toolbar=no,scrollbars=yes'
            );
            if (myWindow) myWindow.focus();
        } catch (e) {
            console.error("Popup bloccati", e);
        }
    }

    static openWidgetByName(name: string): void {
        PageDataFeed.widgetsState[name] = true;
    }

    static closeWidgetByName(name: string): void {
        PageDataFeed.widgetsState[name] = false;
    }

    static isWidgetOpen(name: string): boolean {
        return !!PageDataFeed.widgetsState[name];
    }

    static openEventStats(event: any): void {
        const eventId = event.id.split('/')[2];
        const url = Config.statsURL.replace('{code}', event.providercode || eventId);
        const myWindow = window.open(
            url,
            'stats-' + eventId,
            'width=1032,height=600,location=no,menubar=no,status=no,toolbar=no,scrollbars=yes'
        );
        if (myWindow) myWindow.focus();
    }

    static readPageWidth(): void {
        const width = window.innerWidth;
        const wasMobile = !!PageDataFeed.data.mobile;
        const isMobile = width <= 768;
        if (isMobile !== wasMobile) {
            PageDataFeed.data.mobile = isMobile;
            PageDataFeed.rerender();
        }
    }

    static async getCarousel(): Promise<any> {
        if (PageDataFeed.carouselPromise) return await PageDataFeed.carouselPromise;
        
        PageDataFeed.carouselPromise = new Promise((res, rej) => {
            const pubKey = Config.publickey || 'default';
            $.get(Config.softionAPI + '/home/img/editable/skin-data-' + pubKey + '.json', 
                { json: true, credentials: 'omit' }, 
                (err: any, data: any) => {
                    if (err) return rej(err);
                    res(data);
                }
            );
        });
        return await PageDataFeed.carouselPromise;
    }

    // --- Casino Integration ---
    static setCurrentGame(game: GameEntry | null): void {
        if (game !== null) {
            CasinoFavourites.addPlayed(game);
            BonusNotifierService.notifyIfIsPlayingBonus();
        }
        PageDataFeed.data['current-game'] = game;
        PageDataFeed.events['current-game'].forEach(cb => cb());
    }

    static listenCurrentGame(cb: () => void): void {
        PageDataFeed.events['current-game'].push(cb);
    }

    static stopListenCurrentGame(cb: () => void): void {
        PageDataFeed.events['current-game'] = PageDataFeed.events['current-game'].filter(cb1 => cb1 !== cb);
    }

    // --- Preact Lifecycle ---
    componentDidMount() {
        if (Config.softionAccountsAPI && typeof USER_POPUP !== 'undefined') {
            USER_POPUP.init({ url: Config.softionAccountsAPI });
            USER_POPUP.listen("user-login", () => { PageDataFeed.userLoginAction(); });
        }
        PageDataFeed.readPageWidth();
        window.addEventListener('resize', PageDataFeed.readPageWidth);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', PageDataFeed.readPageWidth);
    }

    componentWillMount() {
        Ticket.listenReset(() => PageDataFeed.rerender());
        PageDataFeed.loadSettings().then(() => {
            PageDataFeed.userLoginAction();
        });
    }

    render() {
        return null;
    }
}